import React, { useMemo } from 'react';
import { useStyles } from './styles';
import { Grid, Typography } from '@material-ui/core';
import ServiceCategoryList from 'components/StrategyRegister/ServiceCategoryList';
import { Form, Formik, FormikHelpers } from 'formik';
import FormikOnError from 'utils/FormikOnError';
import { createStrategytSchema } from './validation';
import FormNavigationButtons from 'components/FormNavigationButtons';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedStrategyModeAction } from 'modules/strategyModeNavigation';
import { createStrategyAction, upadateStrategyAction } from 'modules/strategy';
import { FORM_MODE } from 'shared/enums';
import { RootState, store } from 'store';
import { REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { IStrategyRegister } from './interfaces';
import { makeStrategyInitialValues, strategyRegisterTitles } from './helpers';
import Loading from 'components/Loading';
import { isLoading } from 'utils/loading';
import { openAlert } from 'modules/alertHandler';

const StrategyRegister: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { loading: loadingParameters } = useSelector(
    (state: RootState) => state.parameters,
  );

  const { loading: loadingStrategy, current } = useSelector(
    (state: RootState) => state.strategy,
  );

  const { lists, loading: loadingServiceCategoryList } = useSelector(
    (state: RootState) => state.mailingRuleByServiceCategoryList,
  );

  const { mode, loading: loadingStrategyMode } = useSelector(
    (state: RootState) => state.selectedStrategyModeNavigation,
  );

  const loading = isLoading([
    loadingParameters,
    loadingServiceCategoryList,
    loadingStrategyMode,
    loadingStrategy,
  ]);

  const initialValuesLoading = isLoading([loadingServiceCategoryList]);

  const strategyInitialValues = useMemo(
    () =>
      initialValuesLoading
        ? undefined
        : makeStrategyInitialValues(lists, current),
    [initialValuesLoading, current],
  );

  const handleSubmitCreated = (
    values: IStrategyRegister,
    formikHelpers: FormikHelpers<IStrategyRegister>,
  ) => {
    formikHelpers.setSubmitting(true);

    const action =
      mode === FORM_MODE.create ? createStrategyAction : upadateStrategyAction;

    const nextStatus = values.status === 'CREATED' ? 'SIMULATING' : 'CREATED';

    const actionToDispatch = action({
      strategy: {
        ...values,
        name: values.name.trim(),
        status: nextStatus,
        id: current?.id || undefined,
      },
      onSuccess: () => formikHelpers.setSubmitting(false),
    });

    if (nextStatus === 'CREATED') {
      store.dispatch(
        openAlert({
          title: 'Atenção!',
          content: `Você tem certeza que deseja criar essa estratégia?`,
          primaryButtonAction: actionToDispatch,
          secondaryButton: 'Cancelar',
        }),
      );

      return;
    }

    dispatch(actionToDispatch);
  };

  const handleSubmitDraft = (
    values: IStrategyRegister,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    if (!values.name) {
      store.dispatch(
        openAlert({
          title: 'Atenção!',
          content: `O campo nome é obrigatório para salvar rascunho.`,
        }),
      );
      return;
    }

    setSubmitting(true);

    const action =
      mode === FORM_MODE.create ? createStrategyAction : upadateStrategyAction;

    dispatch(
      action({
        strategy: { ...values, status: 'DRAFT', id: current?.id || undefined },
        onSuccess: () => setSubmitting(false),
      }),
    );
  };

  const handleBackToStrategyList = () => {
    dispatch(setSelectedStrategyModeAction(FORM_MODE.list));
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
    >
      <Grid
        item
        xs={12}
        container
        direction="column"
        justifyContent="flex-start"
        alignItems="stretch"
      >
        {loadingStrategyMode === REQUEST_LIFE_CICLE.SUCCEEDED && (
          <Grid item xs={12}>
            <Typography
              data-cy="page-title-create-strategy__label--title"
              variant="h4"
            >
              {strategyRegisterTitles[mode]} de Estratégia
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} className={classes.content}>
          {loading ? (
            <Loading />
          ) : (
            <Formik
              initialValues={strategyInitialValues}
              validationSchema={createStrategytSchema}
              onSubmit={handleSubmitCreated}
              validateOnChange={false}
              validateOnBlur={true}
            >
              {({ setSubmitting, values }) => (
                <FormikOnError>
                  <Form>
                    <ServiceCategoryList />
                    <Grid item xs={12}>
                      <FormNavigationButtons
                        isViewOnly={mode === FORM_MODE.view}
                        backButtonOnClick={handleBackToStrategyList}
                        submitButtonLabel={
                          (mode === FORM_MODE.create ||
                            mode === FORM_MODE.edit) &&
                          values.status === 'DRAFT'
                            ? 'Cadastrar'
                            : values.status === 'CREATED'
                            ? 'Simular'
                            : 'Atualizar'
                        }
                        isSubmitting={
                          loadingStrategy === REQUEST_LIFE_CICLE.PENDING
                        }
                        secondarySubmitButtonOnClick={() => {
                          handleSubmitDraft(values, setSubmitting);
                        }}
                        secondarySubmitButtonLabel={
                          (mode === FORM_MODE.create ||
                            mode === FORM_MODE.edit) &&
                          values.status === 'DRAFT'
                            ? 'Salvar Rascunho'
                            : undefined
                        }
                      />
                    </Grid>
                  </Form>
                </FormikOnError>
              )}
            </Formik>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default StrategyRegister;
